import React from "react";
import Button from "./Button";
/* Modal — Phase B primitive (refreshed in Phase 2c Containers).
   Absorbs the rich Modal from legacy app.jsx (confirm/prompt/alert variants,
   focus trap, error states) + the empty <Modal> shell in legacy primitives.jsx.

   Props:
     open       bool
     onClose    () => void
     title      ReactNode
     children   body content
     variant    "confirm" | "prompt" | "alert"  (default: confirm)
     size       "sm" (420w) | "md" (560w, default) | "lg" (720w)
     danger     bool — danger-toned primary action
     defaultValue   string (prompt only)
     inputType  string (prompt only)
     confirmLabel   override the localized default ("OK" / "OK")
     cancelLabel    override the localized default ("Annulla" / "Cancel")
     lang       "it" | "en" — used to pick default cancel/confirm labels.
                Defaults to window.__APP_LANG__ if set (the legacy
                ForecastItaly writes it on mount), else "it" (parity with
                pre-2c behavior).
     error      string | null
     onConfirm  (value?) => void
     onClearError  () => void  — clears error when user types
     hideActions  bool — Phase 3b. Omit the built-in Cancel/Confirm
                  actions row. The caller renders its own action row
                  inside children (typically <Form.Actions>). Used by
                  complex forms (AdminUsers, scenarios editor) that
                  need busy-state-aware buttons + custom validation
                  copy. Modal's chrome (backdrop, focus trap, escape,
                  title, error) is reused unchanged.
*/

const { useState, useEffect, useRef } = React;

const DEFAULT_LABELS = {
  it: { confirm: "OK",     cancel: "Annulla" },
  en: { confirm: "OK",     cancel: "Cancel"  },
} as const;

function pickLang(prop?: "it" | "en"): "it" | "en" {
  if (prop === "it" || prop === "en") return prop;
  if (typeof window !== "undefined") {
    const fromWindow = (window as any).__APP_LANG__;
    if (fromWindow === "it" || fromWindow === "en") return fromWindow;
  }
  return "it"; // parity with pre-2c hardcoded default
}

export default function Modal({
  open,
  onClose,
  title,
  message,
  children,
  variant = "confirm",
  size = "md",
  danger = false,
  defaultValue = "",
  inputType = "text",
  confirmLabel,
  cancelLabel,
  lang,
  error = null,
  onConfirm,
  onClearError,
  hideActions = false,
  className = "",
}: any) {
  // Phase 2c — i18n the default labels. Caller-provided labels still win.
  const resolvedLang = pickLang(lang);
  const defaults = DEFAULT_LABELS[resolvedLang];
  const resolvedConfirmLabel = confirmLabel ?? defaults.confirm;
  const resolvedCancelLabel  = cancelLabel  ?? defaults.cancel;
  const [value, setValue] = useState(defaultValue);
  const dialogRef = useRef(null);

  useEffect(() => { if (open) setValue(defaultValue); }, [open, defaultValue]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Focus trap: keep Tab inside the dialog while open.
  useEffect(() => {
    if (!open || !dialogRef.current) return;
    const container = dialogRef.current;
    const prevFocus = document.activeElement;
    const focusables = (): HTMLElement[] => Array.from(
      (container as HTMLElement).querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
      )
    );
    const first = focusables()[0];
    first?.focus?.();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const els = focusables();
      if (!els.length) { e.preventDefault(); return; }
      const f = els[0], l = els[els.length - 1];
      if (e.shiftKey && document.activeElement === f) { e.preventDefault(); l.focus(); }
      else if (!e.shiftKey && document.activeElement === l) { e.preventDefault(); f.focus(); }
    };
    container.addEventListener("keydown", onKey);
    return () => {
      container.removeEventListener("keydown", onKey);
      try { (prevFocus as HTMLElement)?.focus?.(); } catch {}
    };
  }, [open]);

  if (!open) return null;

  const submit = (e) => {
    e?.preventDefault?.();
    if (variant === "prompt") onConfirm?.(value);
    else onConfirm?.(true);
  };

  const promptDisabled = variant === "prompt" && !value;

  return (
    <div className="v2-modal-backdrop" onClick={onClose}>
      <div
        ref={dialogRef}
        className={`v2-modal v2-modal--${size} ${className}`.trim()}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? "v2-modal-title" : undefined}
      >
        {title ? <h3 id="v2-modal-title" className="v2-modal__title">{title}</h3> : null}
        {message != null ? <p className="v2-modal__message">{message}</p> : null}
        {variant === "prompt" ? (
          <form onSubmit={submit}>
            <input
              autoFocus
              type={inputType}
              className="v2-modal__input v2-input v2-input--md"
              value={value}
              onChange={(e) => { setValue(e.target.value); if (error && onClearError) onClearError(); }}
            />
          </form>
        ) : null}
        {children}
        {error ? <div className="v2-modal__error" role="alert">{error}</div> : null}
        {hideActions ? null : (
          <div className="v2-modal__actions">
            {variant !== "alert" ? (
              <Button variant="ghost" size="md" onClick={onClose}>{resolvedCancelLabel}</Button>
            ) : null}
            <Button variant={danger ? "danger" : "primary"} size="md" onClick={submit} disabled={promptDisabled}>{resolvedConfirmLabel}</Button>
          </div>
        )}
      </div>
    </div>
  );
}
